/**
 * Adding to the cart without leaving the page: the "Toevoegen" button of a
 * card that becomes a stepper with an amount that can also be typed, and the
 * round quick-add plus.
 *
 * Both announce a first add with an `cart:added` event on the document, which
 * the added notice listens for.
 */
import { addToCart, setQuantity, replay } from '@theme/cart-api';

const ANIMATED = '.sda-card, .sda-row, .sda-feature, .sda-promo';

function announceAdded(source) {
  replay(source.closest(ANIMATED), 'is-added');
  const { productId, productUrl, productTitle } = source.dataset;
  document.dispatchEvent(new CustomEvent('cart:added', { detail: { productId, productUrl, productTitle, source } }));
}

class AddToCart extends HTMLElement {
  #controller = new AbortController();
  #quantity = 0;
  #busy = false;

  connectedCallback() {
    const { signal } = this.#controller;
    this.#quantity = Number(this.dataset.quantity) || 0;

    this.querySelector('form').addEventListener('submit', (event) => this.#onAdd(event), { signal });
    this.querySelector('.sda-stepper').addEventListener('click', (event) => this.#onStep(event), { signal });
    this.#input.addEventListener('change', () => this.#onType(), { signal });
    this.#input.addEventListener('focus', () => this.#input.select(), { signal });
    this.#input.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Enter') this.#input.blur();
      },
      { signal }
    );
    document.addEventListener('cart:updated', (event) => this.#sync(event.detail.cart), { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  get #input() {
    return this.querySelector('.sda-stepper__n');
  }

  async #onAdd(event) {
    event.preventDefault();
    if (await this.#change(() => addToCart(this.dataset.variantId))) announceAdded(this);
  }

  /** A typed amount replaces the quantity; anything that is not a number is put back. */
  async #onType() {
    const typed = Number.parseInt(this.#input.value, 10);
    if (Number.isNaN(typed) || typed < 0 || typed === this.#quantity) {
      this.#input.value = this.#quantity;
      return;
    }

    const changed = await this.#change(() => setQuantity(this.dataset.variantId, typed));
    if (!changed) this.#input.value = this.#quantity;
  }

  async #onStep(event) {
    const step = Number(event.target.closest('[data-step]')?.dataset.step);
    if (!step) return;

    const next = this.#quantity + step;
    const changed = await this.#change(() => setQuantity(this.dataset.variantId, next));
    if (!changed) return;

    if (step > 0) replay(this.closest(ANIMATED), 'is-added');
    if (next === 0) this.querySelector('button[type="submit"]').focus();
  }

  /** Runs one cart request at a time and reports a failure to assistive technology. */
  async #change(run) {
    if (this.#busy) return false;
    this.#busy = true;
    this.setAttribute('aria-busy', 'true');
    const error = this.querySelector('[data-error]');
    error.textContent = '';

    try {
      await run();
      return true;
    } catch (failure) {
      error.textContent = failure.message;
      return false;
    } finally {
      this.#busy = false;
      this.removeAttribute('aria-busy');
    }
  }

  #sync(cart) {
    const quantity = cart.items
      .filter((item) => String(item.variant_id) === this.dataset.variantId)
      .reduce((sum, item) => sum + item.quantity, 0);
    const hadFocus = this.contains(document.activeElement);

    this.#quantity = quantity;
    this.querySelector('button[type="submit"]').hidden = quantity > 0;
    this.querySelector('.sda-stepper').hidden = quantity === 0;
    if (quantity > 0) this.#input.value = quantity;
    if (hadFocus && quantity > 0 && !this.contains(document.activeElement)) {
      this.querySelector('[data-step="1"]').focus();
    }
  }
}

class QuickAdd extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    this.querySelector('form')?.addEventListener('submit', (event) => this.#onAdd(event), {
      signal: this.#controller.signal,
    });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  async #onAdd(event) {
    event.preventDefault();
    const button = this.querySelector('button[type="submit"]');
    if (button.disabled) return;

    button.disabled = true;
    try {
      await addToCart(this.dataset.variantId);
    } catch {
      button.disabled = false;
      return;
    }

    // Inside the added notice the plus turns into a tick and stays that way.
    if (this.closest('added-notice')) {
      button.classList.add('is-done');
      button.setAttribute('aria-label', button.dataset.addedLabel);
      button.querySelector('[data-icon-add]')?.toggleAttribute('hidden', true);
      button.querySelector('[data-icon-done]')?.toggleAttribute('hidden', false);
    } else {
      button.disabled = false;
    }
    announceAdded(this);
  }
}

customElements.define('add-to-cart', AddToCart);
customElements.define('quick-add', QuickAdd);
